import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import GrowthEngineNudgeCard, {
  getNudgeCardPropsFromRecommendation,
} from 'src/components/growthEngine/GrowthEngineNudgeCard'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import {
  fetchGrowthEngineRecommendations,
  postGrowthEngineRecommendationInteraction,
} from 'src/utils/growthEngineApi'
import { resolveGrowthEngineCtaPath } from 'src/utils/growthEngineCtaRoutes'
import { shouldHidePrimaryNudgeOnVenueChrome } from 'src/utils/growthEngineOneJobPage'
import { markGrowthEngineFillBasketFollowUp } from 'src/utils/growthEngineFollowUpSession'
import {
  clearGrowthEngineKeepHumanAfterTipDismiss,
  markGrowthEngineKeepHumanAfterTipDismiss,
  readGrowthEngineKeepHumanAfterTipDismiss,
} from 'src/utils/growthEngineKeepHumanAfterDismiss'
import {
  GROWTH_ENGINE_NUDGE_REFRESH_DELAY_MS,
  GROWTH_ENGINE_NUDGE_REFRESH_RETRY_MS,
  isGrowthEngineFirstSuccessRefreshReason,
  isOnGrowthEngineCtaLanding,
  subscribeGrowthEngineNudgeRefresh,
} from 'src/utils/growthEngineNudgeRefresh'

/** Session-scoped intentIds already recorded as `shown` (avoid remount spam). */
const shownIntentIdsThisSession = new Set()

/**
 * Fetches and mounts the primary Growth Engine recommendation.
 * Express decides what to show: paywall / habit stay Free-only; paid venues
 * still get a discovery card when they have not done first success.
 *
 * Guided lane (see growthEngine docs/guidedLane.md):
 * Prefer success emits after CTA; route-leave is a safety net. Delay + same-intent
 * suppress (+ one retry). Snooze / dismiss stay quiet.
 */
const GrowthEnginePrimaryNudge = ({
  venueId,
  currentPlan,
  /** Purchasing submenu routes zero layout padding — pad only when a card shows. */
  insetForSubmenu = false,
  className,
  'data-testid': dataTestId = 'growth-engine-primary-nudge',
}) => {
  const history = useHistory()
  const location = useLocation()
  const venueProducts = useSelector(state => state.venueProducts.products)
  const venueProductsLoading = useSelector(
    state => state.venueProducts.isLoading,
  )
  const [recommendation, setRecommendation] = useState(null)
  const [guidedSuccess, setGuidedSuccess] = useState(null)
  const [keptHuman, setKeptHuman] = useState(() =>
    readGrowthEngineKeepHumanAfterTipDismiss(venueId),
  )
  const [busy, setBusy] = useState(false)
  const fetchGeneration = useRef(0)
  const awaitingRefreshRef = useRef(false)
  const lastClickedIntentIdRef = useRef(null)
  const ctaLandingPathRef = useRef(null)
  const hasArrivedAtCtaLandingRef = useRef(false)
  const routeLeaveRefreshScheduledRef = useRef(false)
  const refreshTimerIdsRef = useRef([])
  const venueIdRef = useRef(venueId)
  const pathnameRef = useRef(location?.pathname)
  const searchRef = useRef(location?.search)

  venueIdRef.current = venueId
  pathnameRef.current = location?.pathname
  searchRef.current = location?.search

  const clearRefreshTimers = useCallback(() => {
    refreshTimerIdsRef.current.forEach(id => clearTimeout(id))
    refreshTimerIdsRef.current = []
  }, [])

  const clearGuidedLane = useCallback(() => {
    awaitingRefreshRef.current = false
    lastClickedIntentIdRef.current = null
    ctaLandingPathRef.current = null
    hasArrivedAtCtaLandingRef.current = false
    routeLeaveRefreshScheduledRef.current = false
    clearRefreshTimers()
  }, [clearRefreshTimers])

  const clearRecommendation = useCallback(() => {
    setRecommendation(null)
    setGuidedSuccess(null)
  }, [])

  const dropKeptHuman = useCallback(() => {
    clearGrowthEngineKeepHumanAfterTipDismiss(venueIdRef.current)
    setKeptHuman(null)
  }, [])

  const applyPrimaryRecommendation = useCallback(
    (next, nextGuidance = null) => {
      if (next) dropKeptHuman()
      setRecommendation(next)
      setGuidedSuccess(nextGuidance)
    },
    [dropKeptHuman],
  )

  const keepHumanAfterTipCoolOff = useCallback(() => {
    const payload = markGrowthEngineKeepHumanAfterTipDismiss(venueId, {
      featureId: recommendation?.featureId,
      featureLabel: recommendation?.featureName,
    })
    setKeptHuman(payload)
  }, [recommendation, venueId])

  const recordShown = useCallback(async next => {
    if (!next?.intentId || !venueIdRef.current) return
    if (shownIntentIdsThisSession.has(next.intentId)) return
    shownIntentIdsThisSession.add(next.intentId)
    try {
      await postGrowthEngineRecommendationInteraction(venueIdRef.current, {
        intentId: next.intentId,
        interaction: 'shown',
        featureId: next.featureId,
        platform: 'dashboard',
        metadata: {
          variantIndex: next.variantIndex,
          surface: 'dashboard_primary',
        },
      })
    } catch (err) {
      console.warn('Growth Engine shown interaction failed', err)
    }
  }, [])

  /**
   * Fetch primary recommendation and optionally apply guided-lane rules.
   * @param {{ mode?: 'mount' | 'guided' }} [options]
   * @returns {Promise<'applied'|'same_intent'|'empty'|'skipped'|'stale'>}
   */
  const loadRecommendation = useCallback(
    async ({ mode = 'mount' } = {}) => {
      const activeVenueId = venueIdRef.current

      if (
        shouldHidePrimaryNudgeOnVenueChrome(
          pathnameRef.current,
          searchRef.current,
        )
      ) {
        setRecommendation(null)
        setGuidedSuccess(null)
        return 'skipped'
      }

      if (!activeVenueId) {
        setRecommendation(null)
        setGuidedSuccess(null)
        clearGuidedLane()
        return 'skipped'
      }

      if (mode === 'guided' && !awaitingRefreshRef.current) {
        return 'skipped'
      }

      const generation = ++fetchGeneration.current

      try {
        const result = await fetchGrowthEngineRecommendations(activeVenueId, {
          platform: 'dashboard',
        })
        if (generation !== fetchGeneration.current) return 'stale'

        const next = result.recommendation || null
        const nextGuidance = result.guidedSuccess || null

        if (mode === 'mount') {
          applyPrimaryRecommendation(next, nextGuidance)
          if (next) await recordShown(next)
          return next ? 'applied' : 'empty'
        }

        // Guided: only after CTA, and only show a *different* next intent.
        if (!awaitingRefreshRef.current) {
          return 'skipped'
        }

        if (!next) {
          clearGuidedLane()
          applyPrimaryRecommendation(null, null)
          return 'empty'
        }

        if (
          lastClickedIntentIdRef.current &&
          next.intentId === lastClickedIntentIdRef.current
        ) {
          return 'same_intent'
        }

        clearGuidedLane()
        applyPrimaryRecommendation(next, nextGuidance)
        await recordShown(next)
        return 'applied'
      } catch (err) {
        if (generation === fetchGeneration.current) {
          console.warn('Growth Engine recommendations failed', err)
          if (mode === 'mount') {
            setRecommendation(null)
            setGuidedSuccess(null)
          }
        }
        return 'skipped'
      }
    },
    [applyPrimaryRecommendation, clearGuidedLane, recordShown],
  )

  const scheduleFirstSuccessRefresh = useCallback(() => {
    clearRefreshTimers()
    const timerId = setTimeout(() => {
      loadRecommendation({ mode: 'mount' })
    }, GROWTH_ENGINE_NUDGE_REFRESH_DELAY_MS)
    refreshTimerIdsRef.current.push(timerId)
  }, [clearRefreshTimers, loadRecommendation])

  const scheduleGuidedRefresh = useCallback(() => {
    if (!awaitingRefreshRef.current) return

    clearRefreshTimers()

    const timerId = setTimeout(async () => {
      const outcome = await loadRecommendation({ mode: 'guided' })

      if (outcome === 'same_intent' && awaitingRefreshRef.current) {
        // Ladder not ready yet — one deferred retry, then stay quiet.
        const retryId = setTimeout(async () => {
          const retryOutcome = await loadRecommendation({ mode: 'guided' })
          if (retryOutcome === 'same_intent' && awaitingRefreshRef.current) {
            clearGuidedLane()
          }
        }, GROWTH_ENGINE_NUDGE_REFRESH_RETRY_MS)
        refreshTimerIdsRef.current.push(retryId)
      }
    }, GROWTH_ENGINE_NUDGE_REFRESH_DELAY_MS)

    refreshTimerIdsRef.current.push(timerId)
  }, [clearGuidedLane, clearRefreshTimers, loadRecommendation])

  // Initial fetch when venue / plan changes.
  useEffect(() => {
    clearGuidedLane()
    setRecommendation(null)
    setGuidedSuccess(null)
    setKeptHuman(readGrowthEngineKeepHumanAfterTipDismiss(venueId))

    if (
      !venueId ||
      shouldHidePrimaryNudgeOnVenueChrome(
        pathnameRef.current,
        searchRef.current,
      )
    ) {
      return undefined
    }

    loadRecommendation({ mode: 'mount' })

    return () => {
      clearRefreshTimers()
    }
  }, [
    venueId,
    currentPlan,
    clearGuidedLane,
    clearRefreshTimers,
    loadRecommendation,
  ])

  // Guided lane: product screens / console call requestGrowthEngineNudgeRefresh.
  useEffect(() => {
    return subscribeGrowthEngineNudgeRefresh(event => {
      if (
        shouldHidePrimaryNudgeOnVenueChrome(
          location?.pathname,
          location?.search,
        )
      )
        return
      if (!venueId || String(event.venueId) !== String(venueId)) return
      if (isGrowthEngineFirstSuccessRefreshReason(event.reason)) {
        clearGuidedLane()
        scheduleFirstSuccessRefresh()
        return
      }
      if (!awaitingRefreshRef.current) return
      scheduleGuidedRefresh()
    })
  }, [
    venueId,
    location?.pathname,
    location?.search,
    clearGuidedLane,
    scheduleFirstSuccessRefresh,
    scheduleGuidedRefresh,
  ])

  // Guided lane pass 2: after arriving on the CTA landing route, first leave → refresh.
  // Wait for arrival so the pre-navigation pathname does not look like an immediate leave.
  useEffect(() => {
    if (!awaitingRefreshRef.current) return
    const landing = ctaLandingPathRef.current
    if (!landing || routeLeaveRefreshScheduledRef.current) return

    const pathname = location?.pathname || ''
    if (isOnGrowthEngineCtaLanding(pathname, landing)) {
      hasArrivedAtCtaLandingRef.current = true
      return
    }

    if (!hasArrivedAtCtaLandingRef.current) return

    routeLeaveRefreshScheduledRef.current = true
    scheduleGuidedRefresh()
  }, [location?.pathname, scheduleGuidedRefresh])

  const postInteraction = useCallback(
    async (interaction, metadata = {}) => {
      if (!venueId || !recommendation?.intentId) return
      setBusy(true)
      try {
        await postGrowthEngineRecommendationInteraction(venueId, {
          intentId: recommendation.intentId,
          interaction,
          featureId: recommendation.featureId,
          platform: 'dashboard',
          metadata: {
            variantIndex: recommendation.variantIndex,
            surface: 'dashboard_primary',
            ...metadata,
          },
        })
      } finally {
        setBusy(false)
      }
    },
    [venueId, recommendation],
  )

  const handleSnooze = useCallback(async () => {
    try {
      await postInteraction('snoozed')
    } catch (err) {
      console.warn('Growth Engine snooze failed', err)
    }
    keepHumanAfterTipCoolOff()
    clearGuidedLane()
    clearRecommendation()
  }, [
    postInteraction,
    keepHumanAfterTipCoolOff,
    clearGuidedLane,
    clearRecommendation,
  ])

  const handleDismiss = useCallback(async () => {
    try {
      await postInteraction('dismissed')
    } catch (err) {
      console.warn('Growth Engine dismiss failed', err)
    }
    keepHumanAfterTipCoolOff()
    clearGuidedLane()
    clearRecommendation()
  }, [
    postInteraction,
    keepHumanAfterTipCoolOff,
    clearGuidedLane,
    clearRecommendation,
  ])

  const handleCta = useCallback(async () => {
    const action = recommendation?.cta?.action
    const clickedIntentId = recommendation?.intentId || null
    try {
      await postInteraction('clicked', { ctaAction: action || null })
    } catch (err) {
      console.warn('Growth Engine click failed', err)
    }

    // Place an order → shopping lists (or catalogue if the list is empty).
    // Arm the quiet fill-basket strip for that session.
    const ctaOptions = {}
    if (action === 'open_orders') {
      markGrowthEngineFillBasketFollowUp(venueId)
      if (!venueProductsLoading && venueProducts.length === 0) {
        ctaOptions.shoppingListEmpty = true
      }
    }

    const path = resolveGrowthEngineCtaPath(venueId, action, ctaOptions)

    lastClickedIntentIdRef.current = clickedIntentId
    awaitingRefreshRef.current = true
    ctaLandingPathRef.current = path
    routeLeaveRefreshScheduledRef.current = false
    hasArrivedAtCtaLandingRef.current = Boolean(
      path && isOnGrowthEngineCtaLanding(location?.pathname, path),
    )
    clearRefreshTimers()
    dropKeptHuman()
    clearRecommendation()

    if (path) {
      history.push(path)
    }
  }, [
    location?.pathname,
    recommendation,
    postInteraction,
    venueId,
    dropKeptHuman,
    clearRecommendation,
    clearRefreshTimers,
    history,
    venueProducts,
    venueProductsLoading,
  ])

  if (
    shouldHidePrimaryNudgeOnVenueChrome(location?.pathname, location?.search)
  ) {
    return null
  }

  const cardProps = getNudgeCardPropsFromRecommendation(recommendation, {
    guidedSuccess,
    currentPlan,
  })
  if (!cardProps && !keptHuman) return null

  return (
    <Slot
      className={className}
      data-testid={dataTestId}
      insetForSubmenu={insetForSubmenu ? 1 : 0}
    >
      {cardProps ? (
        <GrowthEngineNudgeCard
          title={cardProps.title}
          body={cardProps.body}
          featureName={cardProps.featureName}
          featureId={cardProps.featureId}
          progress={cardProps.progress}
          ctaLabel={cardProps.ctaLabel}
          honestEmptyNote={cardProps.honestEmptyNote}
          snoozeLabel={cardProps.snoozeLabel}
          dismissLabel={cardProps.dismissLabel}
          onCta={cardProps.ctaAction ? handleCta : undefined}
          onSnooze={handleSnooze}
          onDismiss={handleDismiss}
          busy={busy}
        />
      ) : (
        <GrowthEngineHumanDemoRow
          compact
          featureId={keptHuman.featureId}
          featureLabel={keptHuman.featureLabel}
        />
      )}
    </Slot>
  )
}

const Slot = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing?.md || '16px'};
  max-width: 1100px;
  width: 100%;
  ${({ theme, insetForSubmenu }) =>
    insetForSubmenu
      ? `
    padding: ${theme.spacing?.lg || '16px'} ${theme.spacing?.xxl || '32px'} 0;
    max-width: none;
    @media (max-width: ${theme.breakpoints?.lg || 992}px) {
      padding: ${theme.spacing?.lg || '16px'} ${theme.spacing?.md || '16px'} 0;
    }
  `
      : ''}
`

export default GrowthEnginePrimaryNudge

/** Test helper — reset session shown-tracking between vitest cases. */
export function __resetGrowthEnginePrimaryNudgeSessionForTests() {
  shownIntentIdsThisSession.clear()
}
